//src/app/features/services/addService/serviceCard/index.tsx
import {
  useGetCartItemsQuery,
  useDeleteCartItemMutation,
  useAddCartItemServiceMutation,
} from 'app/services/payments/cart';
import { CartItem, Service } from 'app/services/responseTypes';
import { useGetUserBusinessesQuery } from 'app/services/user';
import { AcceptIcon, ArrowDownSolidIcon, CancelIcon } from 'assets/icons';
import { CustomButton } from 'Components/Auth/CustomButton';
import SelectSearchDropDown from 'Components/Form/SearchDropDown/SelectSearchDropDown';
import SelectMenu from 'Components/Generics/Inputs/SelectMenu';
import { errorToast, successToast } from 'Components/Notifications';
import UserAvatar from 'Components/UserAvatar';
import React, { useEffect, useState } from 'react';
import { string } from 'yup';

export interface IServiceCardProps {
  service: Service;
  isBundle: boolean;
  type: 'Project' | 'Service';
}

const ServiceCard = ({ service, isBundle, type }: IServiceCardProps) => {
  const { data: Cart, isSuccess } = useGetCartItemsQuery(type);
  const [DeleteCartItem] = useDeleteCartItemMutation();
  const [AddCartItem] = useAddCartItemServiceMutation();

  const { data: businesses } = useGetUserBusinessesQuery();
  const [serviceSelected, setServiceSelecd] = useState<{
    name: string | undefined;
    id: string | undefined;
  }>();
  const [isLoading, setIsLoading] = useState(false);

  const businessOptions = [
    { name: 'Personal', id: undefined }, // Default option
    ...(businesses?.map((b) => ({
      name: b.name,
      id: b.id.toString(),
    })) || []),
  ];
  useEffect(() => {
    let item = Cart?.cart_items.filter(
      (cart) => cart.product.id === service.id,
    );
    item &&
      item[0] &&
      setServiceSelecd({
        name: item[0]?.business?.name,
        id: item[0]?.business?.id.toString(),
      });
  }, [isSuccess]);

  return (
    <>
      {isSuccess && (
        <div
          className={`${
            isBundle && 'border border-white rounded-3xl  shadow-lg-all p-8'
          }`}
        >
          <div className="flex justify-between">
            <div>
              <p className=" font-semibold text-gray-dark text-base">
                {service.title}
              </p>
              <span className=" inline-flex">
                <p className=" text-green text-regular  font-semibold">
                  ${service.prices[0].amount_cents / 100}
                </p>{' '}
                <p className=" text-gray-700  text-xs mt-1">
                  {service.prices[0].price_type.name === 'monthly'
                    ? '/ Month'
                    : service.prices[0].price_type.name === 'yearly'
                    ? '/ Year'
                    : ''}
                </p>
              </span>
            </div>
            <div className="w-1/2 h-10">
              <SelectSearchDropDown
                clearable={false}
                options={businessOptions}
                borderInputColor={'border-green'}
                value={
                  serviceSelected?.id
                    ? serviceSelected
                    : { name: 'Personal', id: undefined } // Show default label when no business is selected
                }
                dropDownArrowColor={'text-green'}
                onChange={(e) => {
                  setIsLoading(false);
                  // Handle "Default: Individual" selection
                  if (e.name === 'Personal') {
                    setServiceSelecd({
                      name: 'Select Business',
                      id: undefined,
                    });
                    return;
                  }
                  setServiceSelecd({ name: e.name, id: e.id });
                  Cart?.cart_items.some(
                    (cart) => cart.product.id === service.id,
                  ) &&
                    DeleteCartItem(
                      Cart?.cart_items.find(
                        (cartItem) => cartItem.product.id === service.id,
                      )?.id,
                    );
                }}
                searchFilter={(search, options) =>
                  options.filter((x) => x.name?.includes(search))
                }
                selectedItemSkin={(category) => (
                  <span className="font-semibold text-green">
                    {category.name}
                  </span>
                )}
                optionItemSkin={(category) => category.name}
              />
            </div>
          </div>
          {isBundle ? (
            <span className=" text-gray-700 text-sm py-3 grid">
              {service.features?.map((f) => (
                <div key={f} className="inline-flex py-1">
                  {' '}
                  <span className="mt-1">
                    <AcceptIcon className="text-green" />{' '}
                  </span>{' '}
                  <p className="ml-4"> {f}</p>
                </div>
              ))}
            </span>
          ) : (
            <p className=" text-gray-700 text-sm py-3">{service.description}</p>
          )}
          <div>
            {!Cart?.cart_items.some(
              (cart) => cart.product.id === service.id,
            ) ? (
              <div
                className={`${isBundle ? 'w-full' : 'w-1/4'}    md-max:w-full`}
              >
                <CustomButton
                  text="Add to Cart"
                  textSize="text-sm"
                  height="h-9"
                  width="min-w-max"
                  loading={isLoading}
                  onClick={() => {
                    setIsLoading(true);
                    AddCartItem({
                      cart_item: {
                        product_id: service.id,
                        price_id: service.prices[0].id,
                        business_id: Number(serviceSelected?.id), // Pass `0` for "Personal"
                      },
                    })
                      .unwrap()
                      .then(() => {
                        successToast(
                          `${
                            isBundle ? 'Bundle' : 'Service'
                          } has been added successfully`,
                        );
                      })
                      .catch((e) => {
                        errorToast(
                          e?.data?.message || 'Failed to add to cart.',
                        );
                      })
                      .finally(() => {
                        setIsLoading(false);
                      });
                  }}
                />
              </div>
            ) : (
              <div
                className={`${
                  isBundle ? 'w-full ' : 'w-1/2'
                }  inline-flex gap-3   md-max:w-full md-max:grid`}
              >
                <CustomButton
                  text="Added"
                  textColor="white"
                  bgColor="gray-light"
                  height="h-9"
                  textSize="text-sm"
                  borderColor="gray-light"
                />
                <CustomButton
                  height="h-9"
                  text="Remove"
                  textSize="text-sm"
                  borderColor="gray-light"
                  prefix={<CancelIcon />}
                  textColor="black"
                  bgColor="white"
                  onClick={() => {
                    setIsLoading(false);

                    DeleteCartItem(
                      Cart?.cart_items.find(
                        (cartItem: CartItem) =>
                          cartItem.product.id === service.id,
                      )?.id,
                    )
                      .unwrap()
                      .then(() =>
                        successToast(
                          `${
                            isBundle ? 'bundle' : 'service'
                          } has been removed successfully`,
                        ),
                      )
                      .catch((e) => {
                        errorToast(e.data.message);
                      });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceCard;
